import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { BarChart3, TrendingUp, Award, Printer, FileText, Feather, BookOpen, Briefcase, Sparkles, ArrowRight } from "lucide-react";

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(r => setD(r.data)); }, []);

  if (!d) return <div className="max-w-6xl mx-auto p-8 text-muted-foreground">Loading your analytics…</div>;

  const trend = (d.trend || []).map((t, i) => ({ ...t, i: i + 1 }));

  return (
    <div className="max-w-6xl mx-auto p-8 print:p-0">
      <div className="mb-8 flex items-start justify-between gap-4 print:hidden">
        <div>
          <div className="overline">You over time</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><BarChart3 className="w-10 h-10 text-primary"/>Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Track how your proposal readiness improves over time, and which reviewer criteria consistently need the most work.</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={()=>window.print()} data-testid="analytics-print"><Printer className="w-4 h-4 mr-2"/>Print</Button>
      </div>

      {/* KPI row */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={FileText} label="Proposals" value={d.counts.proposals} color="text-primary" testid="kpi-proposals"/>
        <Kpi icon={BookOpen} label="Papers" value={d.counts.papers} color="text-sky-700" testid="kpi-papers"/>
        <Kpi icon={Feather} label="Theses" value={d.counts.thesis} color="text-emerald-700" testid="kpi-thesis"/>
        <Kpi icon={Briefcase} label="Consultancy" value={d.counts.consultancy} color="text-amber-700" testid="kpi-consultancy"/>
      </div>

      {/* Readiness trend + weakness heatmap */}
      <div className="grid lg:grid-cols-5 gap-6 mb-6">
        <div className="card-crisp rounded-lg p-5 lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Reviewer-readiness over time</div>
              <div className="font-display text-2xl flex items-center gap-2"><TrendingUp className="w-6 h-6 text-primary"/>Score trend</div>
            </div>
            <div className="text-sm text-muted-foreground">{d.counts.readiness_runs} run{d.counts.readiness_runs===1?"":"s"}</div>
          </div>
          {trend.length === 0 ? (
            <EmptyChart hint="Run the Reviewer Score coach on any proposal to start tracking your progress here." link="/app/discovery" cta="Open Discovery Coach"/>
          ) : (
            <div style={{width:"100%", height: 280}} data-testid="analytics-trend">
              <ResponsiveContainer>
                <LineChart data={trend} margin={{ top: 10, right: 12, left: -12, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3}/>
                  <XAxis dataKey="i" tickLine={false} axisLine={false} tick={{fontSize:11}}/>
                  <YAxis domain={[0,100]} tickLine={false} axisLine={false} tick={{fontSize:11}}/>
                  <RTooltip formatter={(v)=>[`${v}/100`, "Readiness"]} labelFormatter={(l,payload)=> payload?.[0]?.payload?.date || `#${l}`}/>
                  <Line type="monotone" dataKey="overall" stroke="#C05A41" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }}/>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="card-crisp rounded-lg p-5 lg:col-span-2">
          <div className="mb-3">
            <div className="overline">Weakness heatmap</div>
            <div className="font-display text-2xl flex items-center gap-2"><Award className="w-6 h-6 text-primary"/>Where you lose points</div>
            <div className="text-xs text-muted-foreground mt-1">Average score per reviewer criterion (lower = more room to improve).</div>
          </div>
          {(!d.weakness_heatmap || d.weakness_heatmap.length===0) ? (
            <EmptyChart hint="Not enough runs yet. Score at least one proposal to see your weakness map." link="/app/discovery" cta="Score a proposal"/>
          ) : (
            <div style={{width:"100%", height: 260}} data-testid="analytics-heatmap">
              <ResponsiveContainer>
                <BarChart data={d.weakness_heatmap} layout="vertical" margin={{ top: 4, right: 10, left: 40, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3}/>
                  <XAxis type="number" domain={[0,100]} tick={{fontSize:11}}/>
                  <YAxis type="category" dataKey="criterion" tick={{fontSize:10}} width={130} tickFormatter={(v)=>v.replace(/_/g,' ')}/>
                  <RTooltip formatter={(v)=>[`${v}/100`, "Avg score"]} />
                  <Bar dataKey="avg" fill="#C05A41" radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Recent proposals */}
      <div className="card-crisp rounded-lg p-5">
        <div className="mb-3">
          <div className="overline">Recent work</div>
          <div className="font-display text-2xl">Proposals you've touched recently</div>
        </div>
        {(!d.recent_proposals || d.recent_proposals.length===0) ? (
          <div className="text-sm text-muted-foreground">Nothing yet — <Link to="/app" className="text-primary hover:underline">start a new proposal</Link>.</div>
        ) : (
          <div className="divide-y divide-border">
            {d.recent_proposals.map(p => (
              <Link key={p.id} to={`/app/proposals/${p.id}`} className="flex items-center justify-between py-2 hover:bg-secondary/50 rounded px-2 -mx-2 group" data-testid={`analytics-recent-${p.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{p.title || "Untitled"}</div>
                  <div className="text-xs text-muted-foreground">Updated {(p.updated_at||"").slice(0,10)}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary"/>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 rounded-lg border-l-4 border-primary bg-primary/5 p-4 text-sm flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5"/>
        <div><b>Tip:</b> Aim for a score that trends upward across submissions. If a criterion in the weakness heatmap sits below 60, book a Discovery Coach session to work on it before your next submission.</div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="card-crisp rounded-lg p-5 flex items-center gap-4" data-testid={testid}>
      <div className={`w-11 h-11 rounded-lg bg-primary/10 grid place-items-center ${color}`}><Icon className="w-5 h-5"/></div>
      <div>
        <div className="overline">{label}</div>
        <div className="font-display text-4xl leading-none" style={{fontFeatureSettings:'"lnum" 1'}}>{value ?? 0}</div>
      </div>
    </div>
  );
}

function EmptyChart({ hint, link, cta }) {
  return (
    <div className="text-center py-10 text-sm text-muted-foreground">
      <p className="mb-3">{hint}</p>
      {link && <Link to={link}><Button size="sm" variant="outline" className="rounded-full">{cta} →</Button></Link>}
    </div>
  );
}
